import React, { useState, useRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import { useTheme } from '@mui/material/styles'
import DoubleArrowIcon from './assets/DoubleArrowRight.svg?react'
import Stack from '@mui/material/Stack'

export interface SlideToActionProps {
    /** Callback when slide is completed */
    onSlideComplete?: () => void
    /** Disable the slider */
    disabled?: boolean
    /** Text to display (will be translated if useTranslate is provided) */
    text?: string
    /** Reset the slider to initial state */
    reset?: boolean
    /** Custom width */
    width?: { xs: string; sm: string }
    /** Custom height */
    height?: number
    /** Background color when not completed */
    bgColor?: string
    /** Background color when completed */
    completedBgColor?: string
    /** Custom icon for slider button */
    icon?: React.ReactNode
    /** Custom icon when completed */
    completedIcon?: React.ReactNode
    /** Additional sx props */
    sx?: SxProps<Theme>
}

const SLIDER_WIDTH = 60
const COMPLETION_THRESHOLD = 0.95

/**
 * SlideToAction Component
 * A swipeable button for confirming important actions (payments, deletions, etc.)
 *
 * @example
 * ```tsx
 * <SlideToAction
 *   text="Slide to Pay"
 *   onSlideComplete={handlePayment}
 * />
 * ```
 */
export const SlideToAction: React.FC<SlideToActionProps> = ({
    onSlideComplete,
    disabled = false,
    text = 'Slide to confirm',
    reset = false,
    width = { xs: '203px', sm: '244px' },
    height = 40,
    bgColor = '#5A8FC3',
    completedBgColor = 'success.main',
    icon: _icon,
    completedIcon: _completedIcon,
    sx
}) => {
    const theme = useTheme()
    const [isDragging, setIsDragging] = useState(false)
    const [slidePosition, setSlidePosition] = useState(0)
    const [isCompleted, setIsCompleted] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const sliderRef = useRef<HTMLDivElement>(null)
    const startPositionRef = useRef(0)
    const isCompletedRef = useRef(false)
    const lastContainerWidthRef = useRef(0)

    const handleStart = (clientX: number) => {
        if (disabled || isCompletedRef.current) return
        setIsDragging(true)
        startPositionRef.current = clientX - slidePosition
    }

    const handleMove = (clientX: number) => {
        if (!isDragging || disabled || isCompletedRef.current) return

        const container = containerRef.current
        if (!container) return

        const containerWidth = container.offsetWidth
        const maxPosition = containerWidth - SLIDER_WIDTH
        const newPosition = Math.max(0, Math.min(maxPosition, clientX - startPositionRef.current))

        setSlidePosition(newPosition)

        if (newPosition >= maxPosition * COMPLETION_THRESHOLD && !isCompletedRef.current) {
            isCompletedRef.current = true
            setIsCompleted(true)
            setSlidePosition(maxPosition)
            onSlideComplete?.()
        }
    }

    const handleEnd = () => {
        if (!isDragging) return
        setIsDragging(false)

        if (isCompletedRef.current) return

        const container = containerRef.current
        if (!container) return

        const containerWidth = container.offsetWidth
        const maxPosition = containerWidth - SLIDER_WIDTH

        if (slidePosition < maxPosition * COMPLETION_THRESHOLD) {
            setSlidePosition(0)
        }
    }

    // Mouse events
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault()
        handleStart(e.clientX)
    }

    const handleMouseMove = (e: MouseEvent) => {
        handleMove(e.clientX)
    }

    const handleMouseUp = () => {
        handleEnd()
    }

    // Touch events
    const handleTouchStart = (e: React.TouchEvent) => {
        handleStart(e.touches[0]?.clientX ?? 0)
    }

    const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault()
        handleMove(e.touches[0]?.clientX ?? 0)
    }

    const handleTouchEnd = () => {
        handleEnd()
    }

    // Handle external reset
    useEffect(() => {
        if (reset) {
            setSlidePosition(0)
            setIsCompleted(false)
            setIsDragging(false)
            isCompletedRef.current = false
        }
    }, [reset])

    // Handle resize and orientation changes
    useEffect(() => {
        const handleResize = () => {
            const container = containerRef.current
            if (!container) return

            const currentWidth = container.offsetWidth
            const lastWidth = lastContainerWidthRef.current

            if (lastWidth > 0 && currentWidth !== lastWidth && slidePosition > 0) {
                const progressRatio = slidePosition / (lastWidth - SLIDER_WIDTH)
                const newMaxPosition = currentWidth - SLIDER_WIDTH
                const newSlidePosition = Math.min(progressRatio * newMaxPosition, newMaxPosition)

                setSlidePosition(newSlidePosition)

                if (isCompletedRef.current) {
                    setSlidePosition(newMaxPosition)
                }
            }

            lastContainerWidthRef.current = currentWidth
        }

        if (containerRef.current) {
            lastContainerWidthRef.current = containerRef.current.offsetWidth
        }

        window.addEventListener('resize', handleResize)
        window.addEventListener('orientationchange', () => {
            setTimeout(handleResize, 100)
        })

        return () => {
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('orientationchange', handleResize)
        }
    }, [slidePosition])

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.addEventListener('touchmove', handleTouchMove, { passive: false })
            document.addEventListener('touchend', handleTouchEnd)
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.removeEventListener('touchmove', handleTouchMove)
            document.removeEventListener('touchend', handleTouchEnd)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging])

    const getContainerOffsetWidth = () => {
        return containerRef.current?.offsetWidth ?? 0
    }

    const progressPercentage = () => {
        const containerWidth = getContainerOffsetWidth()
        return containerWidth ? (slidePosition / (containerWidth - SLIDER_WIDTH)) * 100 : 0
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width,
                height: `${height}px`,
                backgroundColor: isCompleted ? completedBgColor : bgColor,
                borderRadius: `${height / 2}px`,
                overflow: 'hidden',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                userSelect: 'none',
                touchAction: 'none',
                ...sx
            }}>
            {/* Progress background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: '0px',
                    height: '100%',
                    width: `${Math.max(progressPercentage(), 0)}%`,
                    backgroundColor: isCompleted ? completedBgColor : 'transparent',
                    borderRadius: `${height / 2}px`,
                    transition: isDragging ? 'none' : 'width 0.3s ease-out'
                }}
            />

            {/* Text */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: '0',
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1
                }}>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '24px',
                        textAlign: 'center',
                        visibility: isDragging && !isCompleted ? 'hidden' : 'visible'
                    }}>
                    {text}
                </Typography>
            </Box>

            {/* Slider button */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                sx={{
                    position: 'absolute',
                    top: `${height * 0.125}px`,
                    left: `${slidePosition + height * 0.125 + (isCompleted ? -25 : 0)}px`,
                    right: isCompleted ? '0px' : '25px',
                    width: `68px`,
                    height: `${height * 0.75}px`,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: '88px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: disabled ? 'not-allowed' : 'grab',
                    transition: isDragging ? 'none' : 'left 0.3s ease-out',
                    boxShadow: 'none',
                    zIndex: 2,
                    '&:active': {
                        cursor: 'grabbing'
                    }
                }}>
                <DoubleArrowIcon />
            </Stack>
        </Box>
    )
}

export default SlideToAction
